/* Profile: the agency's own details, and the signed-in person's name.
 *
 * What a family sees (the display name), how the platform reaches the agency
 * (phone, email) and the registered office address are the owner's to change;
 * saving puts them in force at once (PUT /agency/profile). The LEGAL name, the
 * city and the approval status are NOT editable here — the first is the
 * incorporation document, the last two are the platform's to decide — so they
 * are shown read-only.
 *
 * Owners and admins edit; anyone else reads (the server refuses the write
 * anyway — hiding the form is not a permission). */

import { useState, type CSSProperties } from 'react';
import { updateProfile, type ProfileEdit } from '../data/agency';
import { canManage, ROLE_WORD } from '../nav';
import { useProfile } from '../shell/profile';
import { Btn, Card, CardBody, CardHead, Field, Input, Note, Stack } from '../ui';
import { Facts, useNotice } from './common';

/* The form is a two-column grid that caps its width and collapses to one
 * column when narrow — so a field is a readable box, not a line stretched the
 * whole width of a desktop. Fields that hold long values span both columns. */
const FORM_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(240px, 1fr))',
  gap: '14px 20px',
  maxWidth: 820,
  marginTop: 4,
};
const SPAN_ALL: CSSProperties = { gridColumn: '1 / -1' };

export function ProfileScreen() {
  const { profile, reload } = useProfile();
  const manage = canManage(profile.me.role);
  const { notice, run } = useNotice();

  const a = profile.agency;
  const [displayName, setDisplayName] = useState(a.display_name);
  const [fullName, setFullName] = useState(profile.me.full_name ?? '');
  const [phone, setPhone] = useState(a.contact_phone_e164 ?? '');
  const [email, setEmail] = useState(a.contact_email ?? '');
  const [landmark, setLandmark] = useState(a.registered_landmark ?? '');
  const [pincode, setPincode] = useState(a.registered_pincode ?? '');
  const [saving, setSaving] = useState(false);

  const dirty =
    displayName !== a.display_name ||
    fullName !== (profile.me.full_name ?? '') ||
    phone !== (a.contact_phone_e164 ?? '') ||
    email !== (a.contact_email ?? '') ||
    landmark !== (a.registered_landmark ?? '') ||
    pincode !== (a.registered_pincode ?? '');

  async function save() {
    const body: ProfileEdit = {
      display_name: displayName.trim(),
      full_name: fullName.trim(),
      contact_phone_e164: phone.trim(),
      contact_email: email.trim(),
      registered_landmark: landmark.trim(),
      registered_pincode: pincode.trim(),
    };
    setSaving(true);
    await run(() => updateProfile(body), 'Your profile is saved.');
    await reload();
    setSaving(false);
  }

  return (
    <Stack>
      {notice}
      {!manage ? (
        <Note tone="amber">Only the agency&apos;s owner or an admin can change these details. You can read them.</Note>
      ) : null}

      <Card>
        <CardHead title="Your details" sub="Your own name on this desk." />
        <CardBody>
          <div style={FORM_GRID}>
            <Field label="Your name" style={SPAN_ALL}>
              <Input value={fullName} onChange={setFullName} placeholder="As you want it shown" />
            </Field>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead
          title="Agency details"
          sub="What families see, and how the platform reaches you. Saved changes take effect at once."
        />
        <CardBody>
          <div style={FORM_GRID}>
            <Field label="Display name (what families see)" style={SPAN_ALL}>
              <Input value={displayName} onChange={setDisplayName} placeholder="Your agency's name" />
            </Field>
            <Field label="Contact phone">
              <Input value={phone} onChange={setPhone} placeholder="10-digit mobile or +91…" type="tel" />
            </Field>
            <Field label="Contact email">
              <Input value={email} onChange={setEmail} placeholder="care@youragency.example" type="email" />
            </Field>
            <Field label="Registered office — landmark">
              <Input value={landmark} onChange={setLandmark} placeholder="Near…, area" />
            </Field>
            <Field label="Registered office — pincode">
              <Input value={pincode} onChange={setPincode} placeholder="6-digit pincode" />
            </Field>
          </div>

          {manage ? (
            <div style={{ marginTop: 18 }}>
              <Btn variant="primary" size="lg" onClick={() => void save()} disabled={!dirty || saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </Btn>
            </div>
          ) : null}
        </CardBody>
      </Card>

      <Card>
        <CardHead title="On the platform" sub="These are set at registration or by the platform, and cannot be changed here." />
        <CardBody>
          <Facts
            items={[
              ['Legal name', a.legal_name],
              ['City', a.city_name || a.city_code],
              ['Your role', ROLE_WORD[profile.me.role] ?? profile.me.role],
              ['Approval status', a.status],
            ]}
          />
        </CardBody>
      </Card>
    </Stack>
  );
}
